import { socketManager, SlotResult, SlotWinningLine, CasinoGameResult } from '../../socket/SocketManager';

const SYMBOLS = ['CHERRY', 'LEMON', 'ORANGE', 'PLUM', 'BAR', 'BELL', 'SEVEN', 'DIAMOND', 'WILD'] as const;

const SYMBOL_COLORS: Record<string, number> = {
  CHERRY: 0xff2222,
  LEMON: 0xf0d000,
  ORANGE: 0xff8833,
  PLUM: 0x9944cc,
  BAR: 0x888899,
  BELL: 0xffd700,
  SEVEN: 0xff2200,
  DIAMOND: 0x22ccee,
  WILD: 0xffd700,
};

const SYMBOL_TEXT_COLORS: Record<string, string> = {
  CHERRY: '#ff4444',
  LEMON: '#f0d000',
  ORANGE: '#ff8833',
  PLUM: '#aa55dd',
  BAR: '#aaaacc',
  BELL: '#ffd700',
  SEVEN: '#ff4400',
  DIAMOND: '#44ddff',
  WILD: '#ffd700',
};

const CHIP_DENOMINATIONS = [10, 25, 50, 100, 500];

const CHIP_COLORS: Record<number, number> = {
  10: 0xcc0000,
  25: 0x00aa00,
  50: 0x0044cc,
  100: 0x222222,
  500: 0x7b2fbe,
};

const GOLD = 0xffd700;
const NEON_GOLD = 0xffdd44;
const DARK_BG = 0x1a1a1a;

const NUM_REELS = 5;
const NUM_ROWS = 3;
const NUM_LINES = 9;

// 9 paylines: each is an array of row indices per reel
const PAYLINES: number[][] = [
  [1, 1, 1, 1, 1], // line 0: middle straight
  [0, 0, 0, 0, 0], // line 1: top straight
  [2, 2, 2, 2, 2], // line 2: bottom straight
  [0, 1, 2, 1, 0], // line 3: V
  [2, 1, 0, 1, 2], // line 4: inverted V
  [0, 0, 1, 2, 2], // line 5: diagonal down-right
  [2, 2, 1, 0, 0], // line 6: diagonal up-right
  [1, 0, 0, 0, 1], // line 7: U top
  [1, 2, 2, 2, 1], // line 8: U bottom
];

const LINE_COLORS: number[] = [
  0xff0000, // red
  0x4488ff, // blue
  0x44ff44, // green
  0xff88ff, // pink
  0xffaa00, // orange
  0x00ffff, // cyan
  0xff44ff, // magenta
  0xffff44, // yellow
  0x88ff88, // lime
];

const PAYOUT_TABLE = [
  { symbol: 'DIAMOND', pay3: 50, pay4: 150, pay5: 500, color: '#44ddff' },
  { symbol: 'SEVEN', pay3: 25, pay4: 75, pay5: 200, color: '#ff4400' },
  { symbol: 'BELL', pay3: 15, pay4: 40, pay5: 100, color: '#ffd700' },
  { symbol: 'BAR', pay3: 8, pay4: 20, pay5: 50, color: '#aaaacc' },
  { symbol: 'PLUM', pay3: 5, pay4: 12, pay5: 25, color: '#aa55dd' },
  { symbol: 'ORANGE', pay3: 3, pay4: 8, pay5: 15, color: '#ff8833' },
  { symbol: 'LEMON', pay3: 3, pay4: 8, pay5: 15, color: '#f0d000' },
  { symbol: 'CHERRY', pay3: 2, pay4: 5, pay5: 10, color: '#ff4444' },
];

export class SlotsGame {
  private betAmount = 0;
  private selectedChip = 100;
  private useDirty = false;
  private elements: Phaser.GameObjects.GameObject[] = [];
  private balanceClean = 0;
  private balanceDirty = 0;
  private balanceText: Phaser.GameObjects.Text | null = null;
  private lastWinText: Phaser.GameObjects.Text | null = null;
  private betDisplayText: Phaser.GameObjects.Text | null = null;
  private linesDisplayText: Phaser.GameObjects.Text | null = null;
  private betPerLineText: Phaser.GameObjects.Text | null = null;
  private winLineText: Phaser.GameObjects.Text | null = null;
  private isSpinning = false;
  private spinBtn: Phaser.GameObjects.Container | null = null;
  private toggleContainer: Phaser.GameObjects.Container | null = null;
  private chipSelectors: Phaser.GameObjects.Container[] = [];
  private timers: Phaser.Time.TimerEvent[] = [];
  private tweens: Phaser.Tweens.Tween[] = [];

  // 5 reels x 3 visible rows
  private reelStripContainers: Phaser.GameObjects.Container[] = [];
  private currentGrid: string[][] = [];  // grid[col][row]

  // Cached layout
  private reelWindowX = 0;
  private reelWindowY = 0;
  private reelWindowW = 0;
  private reelWindowH = 0;
  private cellW = 0;
  private cellH = 0;
  private reelGap = 0;

  private chipSelectorCenterX = 0;
  private chipSelectorY = 0;

  // Spin timers (per reel)
  private spinTimers: Phaser.Time.TimerEvent[] = [];

  // Win animation objects (cleaned between spins)
  private winAnimElements: Phaser.GameObjects.GameObject[] = [];
  private winAnimTimers: Phaser.Time.TimerEvent[] = [];
  private winAnimTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    private scene: Phaser.Scene,
    private graphics: Phaser.GameObjects.Graphics,
    private building: any,
    private stateData: any,
    private onBack: () => void,
  ) {
    try {
      const gameScene = this.scene.scene.get('GameScene') as any;
      if (gameScene) {
        this.balanceDirty = gameScene.moneyDirty ?? 500;
        this.balanceClean = gameScene.moneyClean ?? 0;
      }
    } catch {
      // fallback
    }

    // Initialize grid with random symbols
    this.currentGrid = [];
    for (let col = 0; col < NUM_REELS; col++) {
      this.currentGrid.push(this.randomSymbols(NUM_ROWS));
    }
  }

  private randomSymbols(count: number): string[] {
    const result: string[] = [];
    for (let i = 0; i < count; i++) {
      result.push(SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]);
    }
    return result;
  }

  render(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;

    this.renderRetourButton();
    this.renderMachineHeader(cx, H);
    this.renderReelWindow(cx, W, H);
    this.renderWinLineDisplay(cx, W, H);
    this.renderInfoPanel(cx, W, H);
    this.chipSelectorCenterX = cx;
    this.chipSelectorY = H * 0.69;
    this.renderChipSelector(cx, H * 0.69);
    this.renderControls(cx, W, H);
    this.renderPayoutTable(cx, W, H);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  RETOUR Button (top-left)
  // ═══════════════════════════════════════════════════════════════════

  private renderRetourButton(): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const btnW = Math.max(W * 0.1, 70);
    const btnH = Math.max(H * 0.03, 24);
    const x = W * 0.06;
    const y = H * 0.035;

    const container = this.scene.add.container(x, y).setDepth(120);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x1a1a2e, 0.9);
    bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.3);
    bg.lineStyle(1, GOLD, 0.6);
    bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.3);
    container.add(bg);

    const txt = this.scene.add.text(0, 0, '< RETOUR', {
      fontSize: Math.max(10, Math.round(W * 0.012)) + 'px',
      fontFamily: 'monospace',
      color: '#ffd700',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);

    const hitZone = this.scene.add.zone(0, 0, btnW, btnH)
      .setInteractive({ useHandCursor: true });
    container.add(hitZone);
    hitZone.on('pointerdown', () => this.onBack());
    hitZone.on('pointerover', () => txt.setColor('#ffffff'));
    hitZone.on('pointerout', () => txt.setColor('#ffd700'));

    this.elements.push(container);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Machine Header — "MEGA FORTUNE"
  // ═══════════════════════════════════════════════════════════════════

  private renderMachineHeader(cx: number, H: number): void {
    const W = this.scene.scale.width;
    const y0 = H * 0.02;
    const y1 = H * 0.07;
    const headerH = y1 - y0;
    const headerW = Math.min(W * 0.85, 700);

    this.graphics.fillStyle(0x1a0a2e, 0.9);
    this.graphics.fillRoundedRect(cx - headerW / 2, y0, headerW, headerH, headerH * 0.3);
    this.graphics.lineStyle(2, GOLD, 0.7);
    this.graphics.strokeRoundedRect(cx - headerW / 2, y0, headerW, headerH, headerH * 0.3);

    const titleSize = Math.max(14, Math.round(Math.min(W * 0.032, headerH * 0.6)));
    const title = this.scene.add.text(cx, y0 + headerH * 0.5, 'MEGA FORTUNE', {
      fontSize: titleSize + 'px',
      fontFamily: 'monospace',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#ff8800',
      strokeThickness: Math.max(1, Math.round(titleSize * 0.08)),
      shadow: {
        offsetX: 0,
        offsetY: 0,
        color: '#ffaa00',
        blur: 8,
        fill: true,
      },
    }).setOrigin(0.5).setDepth(101);
    this.elements.push(title);

    const tw = this.scene.tweens.add({
      targets: title,
      alpha: { from: 1, to: 0.75 },
      duration: 1200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.InOut',
    });
    this.tweens.push(tw);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Reel Window (5 reels x 3 rows)
  // ═══════════════════════════════════════════════════════════════════

  private renderReelWindow(cx: number, W: number, H: number): void {
    const y0 = H * 0.07;
    const y1 = H * 0.52;
    const windowH = y1 - y0;
    const windowW = Math.min(W * 0.9, 750);

    this.reelWindowX = cx - windowW / 2;
    this.reelWindowY = y0;
    this.reelWindowW = windowW;
    this.reelWindowH = windowH;

    // Outer gold metallic frame
    this.graphics.fillStyle(0x333348, 1);
    this.graphics.fillRoundedRect(
      this.reelWindowX - 4, y0 - 4,
      windowW + 8, windowH + 8, 12,
    );
    this.graphics.lineStyle(3, GOLD, 0.85);
    this.graphics.strokeRoundedRect(
      this.reelWindowX - 4, y0 - 4,
      windowW + 8, windowH + 8, 12,
    );
    // Outer chrome highlight
    this.graphics.lineStyle(1, 0xaabbcc, 0.3);
    this.graphics.strokeRoundedRect(
      this.reelWindowX - 8, y0 - 8,
      windowW + 16, windowH + 16, 14,
    );

    // Dark reel background
    this.graphics.fillStyle(DARK_BG, 1);
    this.graphics.fillRoundedRect(this.reelWindowX, y0, windowW, windowH, 8);

    // Reel layout: 5 reels with gaps
    const totalGaps = NUM_REELS - 1;
    const reelGap = Math.max(2, windowW * 0.008);
    const reelW = (windowW - totalGaps * reelGap) / NUM_REELS;
    const rowH = windowH / NUM_ROWS;

    this.cellW = reelW;
    this.cellH = rowH;
    this.reelGap = reelGap;

    for (let col = 0; col < NUM_REELS; col++) {
      const reelX = this.reelWindowX + col * (reelW + reelGap);

      // Per-reel slightly lighter bg
      this.graphics.fillStyle(0x111115, 1);
      this.graphics.fillRect(reelX, y0, reelW, windowH);

      // Create mask for this reel
      const maskGraphics = this.scene.add.graphics();
      maskGraphics.fillStyle(0xffffff);
      maskGraphics.fillRect(reelX, y0, reelW, windowH);
      maskGraphics.setVisible(false);
      const mask = maskGraphics.createGeometryMask();
      this.elements.push(maskGraphics);

      // Create reel strip container
      const stripContainer = this.scene.add.container(0, 0).setDepth(102);
      stripContainer.setMask(mask);
      this.reelStripContainers.push(stripContainer);
      this.elements.push(stripContainer);

      // Draw initial symbols
      for (let row = 0; row < NUM_ROWS; row++) {
        const symCenterX = reelX + reelW / 2;
        const symCenterY = y0 + row * rowH + rowH / 2;
        const sym = this.currentGrid[col][row];
        this.drawSymbolAt(stripContainer, sym, symCenterX, symCenterY, reelW * 0.7, rowH * 0.7);
      }

      // Gold separator lines between reels
      if (col < NUM_REELS - 1) {
        const sepX = reelX + reelW + reelGap / 2;
        this.graphics.lineStyle(2, GOLD, 0.35);
        this.graphics.lineBetween(sepX, y0 + 4, sepX, y0 + windowH - 4);
      }
    }

    // Subtle horizontal row separators
    for (let row = 1; row < NUM_ROWS; row++) {
      const lineY = y0 + row * rowH;
      this.graphics.lineStyle(1, 0x333355, 0.4);
      this.graphics.lineBetween(this.reelWindowX + 4, lineY, this.reelWindowX + windowW - 4, lineY);
    }

    // Center payline arrows (left/right edges)
    const paylineY = y0 + windowH / 2;
    const arrowSize = Math.max(6, windowH * 0.025);

    // Left arrow
    this.graphics.fillStyle(GOLD, 0.9);
    this.graphics.beginPath();
    this.graphics.moveTo(this.reelWindowX - 2, paylineY);
    this.graphics.lineTo(this.reelWindowX - 2 - arrowSize, paylineY - arrowSize);
    this.graphics.lineTo(this.reelWindowX - 2 - arrowSize, paylineY + arrowSize);
    this.graphics.closePath();
    this.graphics.fillPath();

    // Right arrow
    this.graphics.beginPath();
    this.graphics.moveTo(this.reelWindowX + windowW + 2, paylineY);
    this.graphics.lineTo(this.reelWindowX + windowW + 2 + arrowSize, paylineY - arrowSize);
    this.graphics.lineTo(this.reelWindowX + windowW + 2 + arrowSize, paylineY + arrowSize);
    this.graphics.closePath();
    this.graphics.fillPath();
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Symbol Drawing (Phaser Graphics)
  // ═══════════════════════════════════════════════════════════════════

  private drawSymbolAt(
    container: Phaser.GameObjects.Container,
    symbol: string,
    cx: number,
    cy: number,
    maxW: number,
    maxH: number,
  ): void {
    const scale = Math.min(maxW / 55, maxH / 55);
    const g = this.scene.add.graphics();
    g.setPosition(cx, cy);

    switch (symbol) {
      case 'CHERRY': {
        const r = 9 * scale;
        // Two red cherries
        g.fillStyle(0xff2222, 1);
        g.fillCircle(-r * 0.7, r * 0.3, r);
        g.fillCircle(r * 0.7, r * 0.3, r);
        // Highlights
        g.fillStyle(0xff6666, 0.5);
        g.fillCircle(-r * 0.7 - r * 0.2, r * 0.3 - r * 0.3, r * 0.35);
        g.fillCircle(r * 0.7 - r * 0.2, r * 0.3 - r * 0.3, r * 0.35);
        // Stems
        g.lineStyle(Math.max(1.5, 2 * scale), 0x228822, 1);
        g.lineBetween(-r * 0.4, -r * 0.4, 0, -r * 1.5);
        g.lineBetween(r * 0.4, -r * 0.4, 0, -r * 1.5);
        // Leaf
        g.fillStyle(0x33aa33, 1);
        g.fillEllipse(r * 0.5, -r * 1.3, r * 0.9, r * 0.45);
        container.add(g);
        break;
      }
      case 'LEMON': {
        const rx = 13 * scale;
        const ry = 9 * scale;
        g.fillStyle(0xf0d000, 1);
        g.fillEllipse(0, 0, rx * 2, ry * 2);
        g.lineStyle(Math.max(1, 1.5 * scale), 0xccaa00, 0.7);
        g.strokeEllipse(0, 0, rx * 2, ry * 2);
        // Highlight
        g.fillStyle(0xffee66, 0.5);
        g.fillEllipse(-rx * 0.25, -ry * 0.25, rx * 0.8, ry * 0.5);
        // Tips
        g.fillStyle(0xe0c000, 1);
        g.fillEllipse(-rx * 0.85, 0, rx * 0.4, ry * 0.35);
        g.fillEllipse(rx * 0.85, 0, rx * 0.4, ry * 0.35);
        container.add(g);
        break;
      }
      case 'ORANGE': {
        const r = 12 * scale;
        g.fillStyle(0xff8833, 1);
        g.fillCircle(0, 0, r);
        g.lineStyle(Math.max(1, 1.5 * scale), 0xcc6622, 0.7);
        g.strokeCircle(0, 0, r);
        // Highlight
        g.fillStyle(0xffbb66, 0.45);
        g.fillCircle(-r * 0.25, -r * 0.3, r * 0.4);
        // Navel
        g.fillStyle(0xcc6622, 0.4);
        g.fillCircle(r * 0.15, r * 0.2, r * 0.15);
        container.add(g);
        break;
      }
      case 'PLUM': {
        const rx = 11 * scale;
        const ry = 13 * scale;
        g.fillStyle(0x9944cc, 1);
        g.fillEllipse(0, 0, rx * 2, ry * 2);
        g.lineStyle(Math.max(1, 1.5 * scale), 0x7722aa, 0.7);
        g.strokeEllipse(0, 0, rx * 2, ry * 2);
        // Highlight
        g.fillStyle(0xcc88ee, 0.4);
        g.fillEllipse(-rx * 0.25, -ry * 0.3, rx * 0.6, ry * 0.4);
        // Stem
        g.lineStyle(Math.max(1, 1.5 * scale), 0x228822, 1);
        g.lineBetween(0, -ry, rx * 0.3, -ry - 4 * scale);
        // Small leaf
        g.fillStyle(0x33aa33, 1);
        g.fillEllipse(rx * 0.4, -ry - 2 * scale, rx * 0.5, ry * 0.2);
        container.add(g);
        break;
      }
      case 'BAR': {
        const bw = 18 * scale;
        const bh = 8 * scale;
        // Metallic bar
        g.fillStyle(0x666680, 1);
        g.fillRoundedRect(-bw, -bh, bw * 2, bh * 2, 3 * scale);
        g.fillStyle(0x9999aa, 0.7);
        g.fillRect(-bw + 2 * scale, -bh, bw * 2 - 4 * scale, 3 * scale);
        g.lineStyle(Math.max(1, 1.5 * scale), 0xaaaacc, 0.9);
        g.strokeRoundedRect(-bw, -bh, bw * 2, bh * 2, 3 * scale);
        container.add(g);
        const fontSize = Math.max(8, Math.round(11 * scale));
        const barText = this.scene.add.text(cx, cy, 'BAR', {
          fontSize: fontSize + 'px',
          fontFamily: 'monospace',
          color: '#ffffff',
          fontStyle: 'bold',
          stroke: '#333344',
          strokeThickness: Math.max(1, Math.round(scale)),
        }).setOrigin(0.5).setDepth(103);
        container.add(barText);
        break;
      }
      case 'BELL': {
        const s = scale;
        const bh = 12 * s;
        const bw = 10 * s;
        // Bell body
        g.fillStyle(0xffd700, 1);
        g.beginPath();
        g.moveTo(-bw, bh * 0.3);
        g.lineTo(-bw * 0.8, -bh * 0.3);
        g.lineTo(-bw * 0.4, -bh * 0.8);
        g.lineTo(bw * 0.4, -bh * 0.8);
        g.lineTo(bw * 0.8, -bh * 0.3);
        g.lineTo(bw, bh * 0.3);
        g.closePath();
        g.fillPath();
        // Bell rim
        g.fillStyle(0xe6c200, 1);
        g.fillRoundedRect(-bw * 1.1, bh * 0.2, bw * 2.2, bh * 0.3, 2 * s);
        // Clapper
        g.fillStyle(0xcc9900, 1);
        g.fillCircle(0, bh * 0.65, bw * 0.2);
        // Highlight
        g.fillStyle(0xffee88, 0.5);
        g.fillEllipse(-bw * 0.3, -bh * 0.3, bw * 0.5, bh * 0.4);
        // Handle
        g.lineStyle(Math.max(1.5, 2 * s), 0xe6c200, 1);
        g.strokeCircle(0, -bh * 0.9, bw * 0.2);
        container.add(g);
        break;
      }
      case 'SEVEN': {
        const s = scale;
        const fontSize7 = Math.max(18, Math.round(30 * s));
        const sevenText = this.scene.add.text(cx, cy, '7', {
          fontSize: fontSize7 + 'px',
          fontFamily: 'serif',
          color: '#ff2200',
          fontStyle: 'bold',
          stroke: '#ffd700',
          strokeThickness: Math.max(2, Math.round(3 * s)),
          shadow: {
            offsetX: 0,
            offsetY: 0,
            color: '#ff6600',
            blur: Math.max(2, Math.round(4 * s)),
            fill: true,
          },
        }).setOrigin(0.5).setDepth(103);
        container.add(sevenText);
        break;
      }
      case 'DIAMOND': {
        const d = 12 * scale;
        // Main diamond shape
        g.fillStyle(0x22ccee, 1);
        g.beginPath();
        g.moveTo(0, -d);
        g.lineTo(d * 0.9, 0);
        g.lineTo(0, d);
        g.lineTo(-d * 0.9, 0);
        g.closePath();
        g.fillPath();
        // Facets
        g.fillStyle(0x55ddff, 0.5);
        g.beginPath();
        g.moveTo(0, -d);
        g.lineTo(d * 0.4, -d * 0.1);
        g.lineTo(0, d * 0.3);
        g.lineTo(-d * 0.4, -d * 0.1);
        g.closePath();
        g.fillPath();
        // Outline
        g.lineStyle(Math.max(1, 1.5 * scale), 0x88eeff, 0.9);
        g.beginPath();
        g.moveTo(0, -d);
        g.lineTo(d * 0.9, 0);
        g.lineTo(0, d);
        g.lineTo(-d * 0.9, 0);
        g.closePath();
        g.strokePath();
        // Sparkle
        g.fillStyle(0xffffff, 0.8);
        g.fillRect(-d * 0.3 - 1 * scale, -d * 0.5, 2 * scale, 5 * scale);
        g.fillRect(-d * 0.3 - 1.5 * scale, -d * 0.5 + 1.5 * scale, 5 * scale, 2 * scale);
        container.add(g);
        break;
      }
      case 'WILD': {
        const s = scale;
        const d = 13 * s;
        // Gold star shape
        g.fillStyle(0xffd700, 1);
        g.beginPath();
        const points = 5;
        for (let i = 0; i < points * 2; i++) {
          const angle = (i * Math.PI) / points - Math.PI / 2;
          const r = i % 2 === 0 ? d : d * 0.45;
          const px = Math.cos(angle) * r;
          const py = Math.sin(angle) * r;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        g.fillPath();
        // Gold outline
        g.lineStyle(Math.max(1, 1.5 * s), 0xffaa00, 1);
        g.beginPath();
        for (let i = 0; i < points * 2; i++) {
          const angle = (i * Math.PI) / points - Math.PI / 2;
          const r = i % 2 === 0 ? d : d * 0.45;
          const px = Math.cos(angle) * r;
          const py = Math.sin(angle) * r;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        g.strokePath();
        // Inner highlight
        g.fillStyle(0xffee88, 0.5);
        g.fillCircle(0, -d * 0.15, d * 0.3);
        container.add(g);
        // WILD text
        const wildFontSize = Math.max(7, Math.round(9 * s));
        const wildText = this.scene.add.text(cx, cy + d * 0.05, 'WILD', {
          fontSize: wildFontSize + 'px',
          fontFamily: 'monospace',
          color: '#ffffff',
          fontStyle: 'bold',
          stroke: '#996600',
          strokeThickness: Math.max(1, Math.round(s)),
        }).setOrigin(0.5).setDepth(103);
        container.add(wildText);
        break;
      }
      default: {
        const txt = this.scene.add.text(cx, cy, '?', {
          fontSize: Math.round(20 * scale) + 'px',
          fontFamily: 'monospace',
          color: '#888888',
          fontStyle: 'bold',
        }).setOrigin(0.5).setDepth(103);
        container.add(txt);
        break;
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Win Display Area
  // ═══════════════════════════════════════════════════════════════════

  private renderWinLineDisplay(cx: number, W: number, H: number): void {
    const y = H * 0.545;
    const fontSize = Math.max(12, Math.round(W * 0.016));

    this.winLineText = this.scene.add.text(cx, y, '', {
      fontSize: fontSize + 'px',
      fontFamily: 'monospace',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 1,
    }).setOrigin(0.5).setDepth(110).setAlpha(0);
    this.elements.push(this.winLineText);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Info Panel — Credits / Bet / Lines / Last Win
  // ═══════════════════════════════════════════════════════════════════

  private renderInfoPanel(cx: number, W: number, H: number): void {
    const y0 = H * 0.57;
    const panelW = Math.min(W * 0.9, 750);
    const panelH = H * 0.08;
    const panelX = cx - panelW / 2;

    this.graphics.fillStyle(0x111122, 0.9);
    this.graphics.fillRoundedRect(panelX, y0, panelW, panelH, 6);
    this.graphics.lineStyle(1, GOLD, 0.3);
    this.graphics.strokeRoundedRect(panelX, y0, panelW, panelH, 6);

    const colCount = 4;
    const colW = panelW / colCount;
    const labelSize = Math.max(8, Math.round(W * 0.01));
    const valueSize = Math.max(12, Math.round(W * 0.015));
    const labelY = y0 + panelH * 0.25;
    const valueY = y0 + panelH * 0.65;

    // CREDITS
    const creditsX = panelX + colW * 0.5;
    const creditsLabel = this.scene.add.text(creditsX, labelY, 'CREDITS', {
      fontSize: labelSize + 'px', fontFamily: 'monospace', color: '#888899', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(creditsLabel);

    this.balanceText = this.scene.add.text(creditsX, valueY, this.getBalanceShort(), {
      fontSize: valueSize + 'px', fontFamily: 'monospace', color: '#44ff88', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(this.balanceText);

    // BET
    const betX = panelX + colW * 1.5;
    const betLabel = this.scene.add.text(betX, labelY, 'BET', {
      fontSize: labelSize + 'px', fontFamily: 'monospace', color: '#888899', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(betLabel);

    this.betDisplayText = this.scene.add.text(betX, valueY, '$0', {
      fontSize: valueSize + 'px', fontFamily: 'monospace', color: '#ffd700', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(this.betDisplayText);

    // LINES
    const linesX = panelX + colW * 2.5;
    const linesLabel = this.scene.add.text(linesX, labelY, 'LINES', {
      fontSize: labelSize + 'px', fontFamily: 'monospace', color: '#888899', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(linesLabel);

    this.linesDisplayText = this.scene.add.text(linesX, valueY, `${NUM_LINES}`, {
      fontSize: valueSize + 'px', fontFamily: 'monospace', color: '#88bbff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(this.linesDisplayText);

    // LAST WIN
    const winX = panelX + colW * 3.5;
    const winLabel = this.scene.add.text(winX, labelY, 'LAST WIN', {
      fontSize: labelSize + 'px', fontFamily: 'monospace', color: '#888899', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(winLabel);

    this.lastWinText = this.scene.add.text(winX, valueY, '$0', {
      fontSize: valueSize + 'px', fontFamily: 'monospace', color: '#ffaa00', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(this.lastWinText);

    // Separators
    for (let i = 1; i < colCount; i++) {
      const sepX = panelX + colW * i;
      this.graphics.lineStyle(1, 0x444466, 0.5);
      this.graphics.lineBetween(sepX, y0 + 6, sepX, y0 + panelH - 6);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Chip Selector
  // ═══════════════════════════════════════════════════════════════════

  private renderChipSelector(centerX: number, y: number): void {
    const W = this.scene.scale.width;
    const chipSpacing = Math.min(W * 0.09, 60);
    const totalW = (CHIP_DENOMINATIONS.length - 1) * chipSpacing;
    const startX = centerX - totalW / 2;
    const chipRadius = Math.min(W * 0.026, 20);
    const currentBalance = this.useDirty ? this.balanceDirty : this.balanceClean;

    CHIP_DENOMINATIONS.forEach((amount, i) => {
      const chipX = startX + i * chipSpacing;
      const container = this.scene.add.container(chipX, y).setDepth(112);

      const color = CHIP_COLORS[amount] || 0x888888;
      const isSelected = this.selectedChip === amount;
      const isAffordable = amount <= currentBalance;

      const g = this.scene.add.graphics();

      if (isSelected) {
        g.lineStyle(Math.max(2, chipRadius * 0.15), NEON_GOLD, 0.9);
        g.strokeCircle(0, 0, chipRadius + Math.max(3, chipRadius * 0.15));
      }

      if (isAffordable) {
        g.fillStyle(color, 1);
      } else {
        g.fillStyle(0x444444, 0.5);
      }
      g.fillCircle(0, 0, chipRadius);

      g.lineStyle(Math.max(1, chipRadius * 0.08), 0xffffff, isAffordable ? 0.9 : 0.3);
      g.strokeCircle(0, 0, chipRadius);

      g.lineStyle(Math.max(0.5, chipRadius * 0.05), 0xffffff, isAffordable ? 0.35 : 0.15);
      g.strokeCircle(0, 0, chipRadius * 0.65);

      // Notch marks
      const notchCount = 8;
      for (let n = 0; n < notchCount; n++) {
        const angle = (n / notchCount) * Math.PI * 2;
        const inner = chipRadius * 0.82;
        const outer = chipRadius * 0.98;
        g.lineStyle(Math.max(1, chipRadius * 0.06), 0xffffff, isAffordable ? 0.4 : 0.15);
        g.lineBetween(
          Math.cos(angle) * inner, Math.sin(angle) * inner,
          Math.cos(angle) * outer, Math.sin(angle) * outer,
        );
      }
      container.add(g);

      const fontSize = Math.max(7, Math.round(chipRadius * 0.55));
      const txt = this.scene.add.text(0, 0, `${amount}`, {
        fontSize: fontSize + 'px',
        fontFamily: 'monospace',
        color: isAffordable ? '#ffffff' : '#666666',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add(txt);

      const hitSize = chipRadius * 2 + 10;
      const hitArea = this.scene.add.zone(0, 0, hitSize, hitSize)
        .setInteractive({ useHandCursor: true });
      container.add(hitArea);

      hitArea.on('pointerdown', () => {
        if (this.isSpinning) return;
        if (this.selectedChip === amount) {
          this.betAmount += amount;
        } else {
          this.selectedChip = amount;
          this.betAmount += amount;
        }
        this.updateBetDisplay();
        this.refreshChipSelector();
      });
      hitArea.on('pointerover', () => container.setScale(1.12));
      hitArea.on('pointerout', () => container.setScale(1.0));

      this.chipSelectors.push(container);
      this.elements.push(container);
    });

    // BET/LINE indicator below chips
    const betPerLineSize = Math.max(8, Math.round(W * 0.009));
    const betPerLine = this.betAmount > 0 ? Math.floor(this.betAmount / NUM_LINES) : 0;
    this.betPerLineText = this.scene.add.text(centerX, y + chipRadius + Math.max(8, W * 0.012), `BET/LINE: $${betPerLine}`, {
      fontSize: betPerLineSize + 'px',
      fontFamily: 'monospace',
      color: '#888899',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(this.betPerLineText);
  }

  private refreshChipSelector(): void {
    // Remove bet/line text too
    if (this.betPerLineText) {
      try { this.betPerLineText.destroy(); } catch { /* ok */ }
      this.betPerLineText = null;
    }
    for (const c of this.chipSelectors) {
      try { c.destroy(); } catch { /* ok */ }
    }
    this.chipSelectors = [];
    this.renderChipSelector(this.chipSelectorCenterX, this.chipSelectorY);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Controls — SPIN, Propre/Sale, RAZ
  // ═══════════════════════════════════════════════════════════════════

  private renderControls(cx: number, W: number, H: number): void {
    const y = H * 0.78;

    // Toggle Propre/Sale (left)
    this.renderToggle(cx - W * 0.24, y);

    // Big SPIN button (center)
    this.renderSpinButton(cx, y);

    // RAZ button (right)
    this.renderRAZButton(cx + W * 0.24, y);
  }

  private renderSpinButton(cx: number, y: number): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const btnW = Math.min(W * 0.2, 140);
    const btnH = Math.min(H * 0.06, 48);

    this.spinBtn = this.scene.add.container(cx, y).setDepth(115);

    // Shadow
    const shadow = this.scene.add.graphics();
    shadow.fillStyle(0x440000, 0.5);
    shadow.fillRoundedRect(-btnW / 2 + 2, -btnH / 2 + 2, btnW, btnH, btnH * 0.3);
    this.spinBtn.add(shadow);

    // Body
    const bg = this.scene.add.graphics();
    bg.fillStyle(0xbb0000, 1);
    bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.3);
    bg.fillStyle(0xdd2222, 0.6);
    bg.fillRoundedRect(-btnW / 2 + 2, -btnH / 2 + 1, btnW - 4, btnH * 0.4, { tl: btnH * 0.3, tr: btnH * 0.3, bl: 0, br: 0 });
    bg.lineStyle(Math.max(2, btnH * 0.06), GOLD, 0.9);
    bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.3);
    this.spinBtn.add(bg);

    const fontSize = Math.max(14, Math.round(btnH * 0.5));
    const txt = this.scene.add.text(0, 0, 'SPIN', {
      fontSize: fontSize + 'px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#880000',
      strokeThickness: Math.max(1, Math.round(fontSize * 0.08)),
    }).setOrigin(0.5);
    this.spinBtn.add(txt);

    const hitZone = this.scene.add.zone(0, 0, btnW, btnH)
      .setInteractive({ useHandCursor: true });
    this.spinBtn.add(hitZone);

    hitZone.on('pointerdown', () => this.spin());
    hitZone.on('pointerover', () => {
      if (!this.isSpinning) this.spinBtn!.setScale(1.06);
    });
    hitZone.on('pointerout', () => this.spinBtn!.setScale(1.0));

    this.elements.push(this.spinBtn);
  }

  private renderToggle(x: number, y: number): void {
    const W = this.scene.scale.width;
    const toggleW = Math.min(W * 0.12, 90);
    const toggleH = Math.min(W * 0.04, 28);

    this.toggleContainer = this.scene.add.container(x, y).setDepth(110);

    const g = this.scene.add.graphics();
    const bgColor = this.useDirty ? 0x442222 : 0x224422;
    g.fillStyle(bgColor, 1);
    g.fillRoundedRect(-toggleW / 2, -toggleH / 2, toggleW, toggleH, toggleH * 0.45);
    g.lineStyle(Math.max(1, toggleH * 0.07), this.useDirty ? 0xff4444 : 0x44ff44, 0.8);
    g.strokeRoundedRect(-toggleW / 2, -toggleH / 2, toggleW, toggleH, toggleH * 0.45);
    this.toggleContainer.add(g);

    const fontSize = Math.max(9, Math.round(toggleH * 0.45));
    const label = this.scene.add.text(0, 0, this.useDirty ? 'SALE' : 'PROPRE', {
      fontSize: fontSize + 'px',
      fontFamily: 'monospace',
      color: this.useDirty ? '#ff4444' : '#44ff44',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    this.toggleContainer.add(label);

    const hitZone = this.scene.add.zone(0, 0, toggleW, toggleH)
      .setInteractive({ useHandCursor: true });
    this.toggleContainer.add(hitZone);

    hitZone.on('pointerdown', () => {
      if (this.isSpinning) return;
      this.useDirty = !this.useDirty;
      const px = this.toggleContainer!.x;
      const py = this.toggleContainer!.y;
      this.toggleContainer!.destroy();
      this.elements = this.elements.filter(e => e !== this.toggleContainer);
      this.renderToggle(px, py);
      this.updateBalanceDisplay();
      this.refreshChipSelector();
    });

    this.elements.push(this.toggleContainer);
  }

  private renderRAZButton(x: number, y: number): void {
    const W = this.scene.scale.width;
    const btnW = Math.min(W * 0.1, 70);
    const btnH = Math.min(W * 0.04, 28);

    const container = this.scene.add.container(x, y).setDepth(110);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x331111, 1);
    bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.3);
    bg.lineStyle(1, 0xff4444, 0.5);
    bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, btnH * 0.3);
    container.add(bg);

    const fontSize = Math.max(9, Math.round(btnH * 0.5));
    const txt = this.scene.add.text(0, 0, 'RAZ', {
      fontSize: fontSize + 'px',
      fontFamily: 'monospace',
      color: '#ff6666',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add(txt);

    const hitZone = this.scene.add.zone(0, 0, btnW, btnH)
      .setInteractive({ useHandCursor: true });
    container.add(hitZone);

    hitZone.on('pointerdown', () => {
      if (this.isSpinning) return;
      this.betAmount = 0;
      this.updateBetDisplay();
      this.refreshChipSelector();
    });
    hitZone.on('pointerover', () => container.setAlpha(0.8));
    hitZone.on('pointerout', () => container.setAlpha(1));

    this.elements.push(container);
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Payout Table
  // ═══════════════════════════════════════════════════════════════════

  private renderPayoutTable(cx: number, W: number, H: number): void {
    const y0 = H * 0.83;
    const tableW = Math.min(W * 0.9, 750);
    const tableH = H * 0.14;
    const tableX = cx - tableW / 2;

    this.graphics.fillStyle(0x0e0e20, 0.9);
    this.graphics.fillRoundedRect(tableX, y0, tableW, tableH, 6);
    this.graphics.lineStyle(1, GOLD, 0.3);
    this.graphics.strokeRoundedRect(tableX, y0, tableW, tableH, 6);

    const titleSize = Math.max(8, Math.round(W * 0.01));
    const titleTxt = this.scene.add.text(cx, y0 + tableH * 0.08, 'PAYOUT TABLE (3/4/5 matches)', {
      fontSize: titleSize + 'px',
      fontFamily: 'monospace',
      color: '#ffd700',
      fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(110);
    this.elements.push(titleTxt);

    // Layout: 3 columns, 3 rows
    const cols = 3;
    const rows = 3;
    const colWidth = tableW / cols;
    const entryFontSize = Math.max(7, Math.round(W * 0.009));
    const startY = y0 + tableH * 0.22;
    const rowSpacing = (tableH * 0.72) / rows;

    // 8 payout entries + WILD note = 9 entries total, laid out in 3x3 grid
    const allEntries = [
      ...PAYOUT_TABLE.map(p => ({ text: `${p.symbol}: ${p.pay3}/${p.pay4}/${p.pay5}`, color: p.color })),
      { text: 'WILD: subs all', color: '#ffd700' },
    ];

    allEntries.forEach((entry, idx) => {
      const col = idx % cols;
      const row = Math.floor(idx / cols);
      const ex = tableX + colWidth * col + colWidth * 0.5;
      const ey = startY + row * rowSpacing;

      const t = this.scene.add.text(ex, ey, entry.text, {
        fontSize: entryFontSize + 'px',
        fontFamily: 'monospace',
        color: entry.color,
        fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(110);
      this.elements.push(t);
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Bet / Balance Display Updates
  // ═══════════════════════════════════════════════════════════════════

  private updateBetDisplay(): void {
    if (this.betDisplayText) {
      this.betDisplayText.setText(this.betAmount > 0 ? `$${this.betAmount}` : '$0');
    }
    if (this.betPerLineText) {
      const betPerLine = this.betAmount > 0 ? Math.floor(this.betAmount / NUM_LINES) : 0;
      this.betPerLineText.setText(`BET/LINE: $${betPerLine}`);
    }
  }

  private updateBalanceDisplay(): void {
    if (this.balanceText) {
      this.balanceText.setText(this.getBalanceShort());
    }
  }

  private getBalanceShort(): string {
    if (this.useDirty) {
      return `$${Math.floor(this.balanceDirty)}`;
    }
    return `$${Math.floor(this.balanceClean)}`;
  }

  // ═══════════════════════════════════════════════════════════════════
  //  SPIN — Server Communication + Reel Animation
  // ═══════════════════════════════════════════════════════════════════

  private spin(): void {
    if (this.isSpinning) return;

    if (this.betAmount <= 0) {
      if (this.winLineText) {
        this.winLineText.setText('Place a bet first!');
        this.winLineText.setColor('#ff8800');
        this.winLineText.setAlpha(1);
      }
      return;
    }

    console.log(`[SLOTS] SPIN clicked: betAmount=${this.betAmount}, useDirty=${this.useDirty}`);
    this.isSpinning = true;

    if (this.spinBtn) this.spinBtn.setAlpha(0.5);

    // Clear previous win animations
    this.clearWinAnimations();

    // Clear win display
    if (this.winLineText) {
      this.winLineText.setAlpha(0);
      this.winLineText.setText('');
    }

    // Send action
    socketManager.sendAction('CASINO_PLAY_SLOTS', {
      buildingId: this.building.id,
      betAmount: this.betAmount,
      useDirty: this.useDirty,
    });

    // Start spinning animation immediately
    this.startReelSpin();

    // Listen for result
    socketManager.onceActionResult((result) => {
      if (result.success && result.data) {
        const gameResult = result.data as unknown as CasinoGameResult;
        const slotResult = gameResult.details as SlotResult;
        console.log(`[SLOTS] Result received: win=${slotResult.isWin}, payout=${slotResult.payout}, grid=${JSON.stringify(slotResult.grid)}, winningLines=${JSON.stringify(slotResult.winningLines)}`);

        // Update balances
        this.balanceClean = gameResult.newClean;
        this.balanceDirty = gameResult.newDirty;

        // Stop reels with result
        this.stopReels(slotResult);
      } else {
        console.log(`[SLOTS] Error: ${result.error || 'unknown error'}`);
        this.stopReelsImmediate();
        this.isSpinning = false;
        if (this.spinBtn) this.spinBtn.setAlpha(1);

        if (this.winLineText) {
          this.winLineText.setText(result.error || 'Erreur');
          this.winLineText.setColor('#ff4444');
          this.winLineText.setAlpha(1);
        }
      }
    });
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Reel Spinning Animation
  // ═══════════════════════════════════════════════════════════════════

  private startReelSpin(): void {
    // CRITICAL: Clear all existing spin timers before starting new ones
    for (const t of this.spinTimers) {
      try { t.destroy(); } catch { /* ok */ }
    }
    this.spinTimers = [];

    for (let col = 0; col < NUM_REELS; col++) {
      const container = this.reelStripContainers[col];
      if (!container) continue;

      const timer = this.scene.time.addEvent({
        delay: 55,
        callback: () => {
          const symbols = this.randomSymbols(NUM_ROWS);
          this.redrawReelSymbols(col, symbols);
        },
        loop: true,
      });
      this.spinTimers.push(timer);
      this.timers.push(timer);
    }
  }

  private redrawReelSymbols(colIndex: number, symbols: string[]): void {
    const container = this.reelStripContainers[colIndex];
    if (!container) return;

    container.removeAll(true);

    const reelW = this.cellW;
    const rowH = this.cellH;
    const reelX = this.reelWindowX + colIndex * (reelW + this.reelGap);
    const y0 = this.reelWindowY;

    // Random vertical offset for motion feel
    const offsetY = (Math.random() - 0.5) * rowH * 0.15;

    for (let row = 0; row < NUM_ROWS; row++) {
      const symCenterX = reelX + reelW / 2;
      const symCenterY = y0 + row * rowH + rowH / 2 + offsetY;
      this.drawSymbolAt(container, symbols[row], symCenterX, symCenterY, reelW * 0.7, rowH * 0.7);
    }

    // Blur overlay for motion
    const blurG = this.scene.add.graphics();
    blurG.fillStyle(DARK_BG, 0.2);
    blurG.fillRect(reelX, y0, reelW, rowH * NUM_ROWS);
    container.add(blurG);
  }

  private stopReels(result: SlotResult): void {
    let reelsComplete = 0;

    for (let col = 0; col < NUM_REELS; col++) {
      // Stop cascade: 600, 900, 1200, 1500, 1800ms
      const stopDelay = 600 + col * 300;

      const stopTimer = this.scene.time.addEvent({
        delay: stopDelay,
        callback: () => {
          // Stop this reel's spin timer
          if (this.spinTimers[col]) {
            this.spinTimers[col].destroy();
            this.timers = this.timers.filter(t => t !== this.spinTimers[col]);
          }

          const container = this.reelStripContainers[col];
          if (!container) return;

          // Use result grid for this reel column
          const finalSymbols = result.grid[col] || this.randomSymbols(NUM_ROWS);

          container.removeAll(true);

          const reelW = this.cellW;
          const rowH = this.cellH;
          const reelX = this.reelWindowX + col * (reelW + this.reelGap);
          const y0 = this.reelWindowY;

          for (let row = 0; row < NUM_ROWS; row++) {
            const symCenterX = reelX + reelW / 2;
            const symCenterY = y0 + row * rowH + rowH / 2;
            this.drawSymbolAt(container, finalSymbols[row], symCenterX, symCenterY, reelW * 0.7, rowH * 0.7);
          }

          this.currentGrid[col] = [...finalSymbols];

          // Bounce effect
          container.setY(-rowH * 0.15);
          const tw = this.scene.tweens.add({
            targets: container,
            y: 0,
            duration: 350,
            ease: 'Bounce.Out',
          });
          this.tweens.push(tw);

          reelsComplete++;
          if (reelsComplete === NUM_REELS) {
            this.onReelsComplete(result);
          }
        },
      });
      this.timers.push(stopTimer);
    }
  }

  private stopReelsImmediate(): void {
    for (const timer of this.spinTimers) {
      try { timer.destroy(); } catch { /* ok */ }
    }
    this.spinTimers = [];

    for (let col = 0; col < NUM_REELS; col++) {
      const container = this.reelStripContainers[col];
      if (!container) continue;
      container.removeAll(true);

      const reelW = this.cellW;
      const rowH = this.cellH;
      const reelX = this.reelWindowX + col * (reelW + this.reelGap);
      const y0 = this.reelWindowY;

      for (let row = 0; row < NUM_ROWS; row++) {
        const symCenterX = reelX + reelW / 2;
        const symCenterY = y0 + row * rowH + rowH / 2;
        this.drawSymbolAt(container, this.currentGrid[col][row], symCenterX, symCenterY, reelW * 0.7, rowH * 0.7);
      }
    }
  }

  private onReelsComplete(result: SlotResult): void {
    console.log(`[SLOTS] Animation ended, win=${result.isWin}, payout=${result.payout}, winningLines=${result.winningLines?.length || 0}`);
    this.updateBalanceDisplay();

    // Reset bet
    this.betAmount = 0;
    this.updateBetDisplay();

    if (result.isWin && result.winningLines && result.winningLines.length > 0) {
      this.showWinCelebration(result);
    } else {
      if (this.winLineText) {
        this.winLineText.setText('No win');
        this.winLineText.setColor('#ff4444');
        this.winLineText.setAlpha(1);
      }
      if (this.lastWinText) {
        this.lastWinText.setText('$0');
      }
      this.isSpinning = false;
      if (this.spinBtn) this.spinBtn.setAlpha(1);
      this.refreshChipSelector();
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Win Celebration
  // ═══════════════════════════════════════════════════════════════════

  private clearWinAnimations(): void {
    for (const t of this.winAnimTimers) {
      try { t.destroy(); } catch { /* ok */ }
    }
    this.winAnimTimers = [];
    for (const tw of this.winAnimTweens) {
      try { tw.stop(); } catch { /* ok */ }
    }
    this.winAnimTweens = [];
    for (const e of this.winAnimElements) {
      try { e.destroy(); } catch { /* ok */ }
    }
    this.winAnimElements = [];
  }

  private showWinCelebration(result: SlotResult): void {
    const W = this.scene.scale.width;
    const H = this.scene.scale.height;
    const cx = W / 2;
    const totalPayout = result.payout;
    const winLines = result.winningLines;

    // Update last win display
    if (this.lastWinText) {
      this.lastWinText.setText(`$${totalPayout}`);
      this.lastWinText.setColor('#ffd700');
    }

    // Cycle through winning lines one by one
    let currentLineIdx = 0;
    const lineCount = winLines.length;

    const showNextLine = () => {
      if (currentLineIdx >= lineCount) {
        // After cycling all lines, show total win
        this.showTotalWin(totalPayout);
        return;
      }

      const wl = winLines[currentLineIdx];
      this.showSingleWinLine(wl, () => {
        currentLineIdx++;
        showNextLine();
      });
    };

    showNextLine();

    // Coin particles
    this.spawnCoinParticles(cx, H);
  }

  private showSingleWinLine(wl: SlotWinningLine, onComplete: () => void): void {
    const lineIndex = wl.lineIndex;
    const symbol = wl.symbol;
    const count = wl.count;
    const payout = wl.payout;
    const lineColor = LINE_COLORS[lineIndex % LINE_COLORS.length];

    // Show line info in win display
    if (this.winLineText) {
      this.winLineText.setText(`Line ${lineIndex + 1}: ${count}x ${symbol} = $${payout}`);
      this.winLineText.setColor('#ffd700');
      this.winLineText.setAlpha(1);

      // Pulsing effect
      const pulse = this.scene.tweens.add({
        targets: this.winLineText,
        alpha: { from: 1, to: 0.5 },
        duration: 250,
        yoyo: true,
        repeat: 2,
        ease: 'Sine.InOut',
      });
      this.winAnimTweens.push(pulse);
    }

    // Get payline row positions
    const payline = PAYLINES[lineIndex];
    if (!payline) {
      const endTimer = this.scene.time.addEvent({ delay: 800, callback: onComplete });
      this.winAnimTimers.push(endTimer);
      this.timers.push(endTimer);
      return;
    }

    // Highlight winning symbols (glow effect)
    const reelW = this.cellW;
    const rowH = this.cellH;
    const glowElements: Phaser.GameObjects.Graphics[] = [];

    for (let col = 0; col < count; col++) {
      const row = payline[col];
      const cellX = this.reelWindowX + col * (reelW + this.reelGap);
      const cellY = this.reelWindowY + row * rowH;

      const glowG = this.scene.add.graphics();
      glowG.fillStyle(lineColor, 0.2);
      glowG.fillRect(cellX, cellY, reelW, rowH);
      glowG.lineStyle(Math.max(2, reelW * 0.02), lineColor, 0.9);
      glowG.strokeRect(cellX + 1, cellY + 1, reelW - 2, rowH - 2);
      glowG.setDepth(108);
      this.winAnimElements.push(glowG);
      this.elements.push(glowG);
      glowElements.push(glowG);
    }

    // Draw payline path connecting winning positions
    const lineG = this.scene.add.graphics();
    lineG.lineStyle(Math.max(2, reelW * 0.015), lineColor, 0.85);
    lineG.beginPath();

    for (let col = 0; col < count; col++) {
      const row = payline[col];
      const px = this.reelWindowX + col * (reelW + this.reelGap) + reelW / 2;
      const py = this.reelWindowY + row * rowH + rowH / 2;
      if (col === 0) {
        lineG.moveTo(px, py);
      } else {
        lineG.lineTo(px, py);
      }
    }
    lineG.strokePath();
    lineG.setDepth(107);
    this.winAnimElements.push(lineG);
    this.elements.push(lineG);

    // Floating payout text rising from the center of winning area
    const floatX = this.reelWindowX + (count - 1) * (reelW + this.reelGap) / 2 + reelW / 2;
    const floatY = this.reelWindowY + this.reelWindowH / 2;
    const floatFontSize = Math.max(14, Math.round(this.scene.scale.width * 0.022));
    const floatText = this.scene.add.text(floatX, floatY, `+$${payout}`, {
      fontSize: floatFontSize + 'px',
      fontFamily: 'monospace',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5).setDepth(115);
    this.winAnimElements.push(floatText);
    this.elements.push(floatText);

    const floatTw = this.scene.tweens.add({
      targets: floatText,
      y: floatY - this.scene.scale.height * 0.06,
      alpha: { from: 1, to: 0 },
      duration: 1000,
      ease: 'Quad.Out',
      onComplete: () => {
        try { floatText.destroy(); } catch { /* ok */ }
      },
    });
    this.winAnimTweens.push(floatTw);

    // After a delay, remove this line's glow and proceed to next
    const cleanupTimer = this.scene.time.addEvent({
      delay: 1200,
      callback: () => {
        for (const gl of glowElements) {
          try { gl.destroy(); } catch { /* ok */ }
        }
        try { lineG.destroy(); } catch { /* ok */ }
        onComplete();
      },
    });
    this.winAnimTimers.push(cleanupTimer);
    this.timers.push(cleanupTimer);
  }

  private showTotalWin(totalPayout: number): void {
    if (this.winLineText) {
      this.winLineText.setText(`TOTAL WIN: $${totalPayout}`);
      this.winLineText.setColor('#ffd700');
      this.winLineText.setAlpha(1);
      this.winLineText.setScale(0.5);

      const tw = this.scene.tweens.add({
        targets: this.winLineText,
        scaleX: 1.15,
        scaleY: 1.15,
        duration: 350,
        ease: 'Back.Out',
        onComplete: () => {
          const tw2 = this.scene.tweens.add({
            targets: this.winLineText,
            scaleX: 1,
            scaleY: 1,
            duration: 200,
            ease: 'Sine.Out',
          });
          this.winAnimTweens.push(tw2);
        },
      });
      this.winAnimTweens.push(tw);

      // Pulsing glow
      const pulse = this.scene.tweens.add({
        targets: this.winLineText,
        alpha: { from: 1, to: 0.6 },
        duration: 400,
        yoyo: true,
        repeat: 3,
        ease: 'Sine.InOut',
      });
      this.winAnimTweens.push(pulse);
    }

    // End celebration after delay
    const endTimer = this.scene.time.addEvent({
      delay: 2000,
      callback: () => {
        this.isSpinning = false;
        if (this.spinBtn) this.spinBtn.setAlpha(1);
        this.refreshChipSelector();
      },
    });
    this.winAnimTimers.push(endTimer);
    this.timers.push(endTimer);
  }

  private spawnCoinParticles(centerX: number, H: number): void {
    const W = this.scene.scale.width;
    for (let i = 0; i < 20; i++) {
      const coin = this.scene.add.graphics();
      const coinR = Math.max(3, W * 0.005);

      coin.fillStyle(0xe6b800, 1);
      coin.fillCircle(0, 0, coinR);
      coin.lineStyle(Math.max(0.5, coinR * 0.15), 0xffdd44, 1);
      coin.strokeCircle(0, 0, coinR);
      coin.fillStyle(0xaa8800, 0.6);
      coin.fillRect(-coinR * 0.15, -coinR * 0.5, coinR * 0.3, coinR);
      coin.setDepth(120);

      const startX = centerX + (Math.random() - 0.5) * W * 0.5;
      const startY = H * 0.42 + Math.random() * H * 0.08;
      coin.setPosition(startX, startY);
      coin.setAlpha(0.95);
      this.winAnimElements.push(coin);
      this.elements.push(coin);

      const tw = this.scene.tweens.add({
        targets: coin,
        y: startY + H * 0.2 + Math.random() * H * 0.15,
        x: startX + (Math.random() - 0.5) * W * 0.14,
        alpha: 0,
        duration: 1000 + Math.random() * 800,
        delay: i * 50,
        ease: 'Quad.In',
        onComplete: () => { try { coin.destroy(); } catch { /* ok */ } },
      });
      this.winAnimTweens.push(tw);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  //  Destroy
  // ═══════════════════════════════════════════════════════════════════

  destroy(): void {
    // Stop spin timers
    for (const t of this.spinTimers) {
      try { t.destroy(); } catch { /* ok */ }
    }
    this.spinTimers = [];

    // Clear win animations
    this.clearWinAnimations();

    this.timers.forEach(t => { try { t.destroy(); } catch { /* ok */ } });
    this.timers = [];

    this.tweens.forEach(t => { try { t.stop(); } catch { /* ok */ } });
    this.tweens = [];

    this.chipSelectors.forEach(c => { try { c.destroy(); } catch { /* ok */ } });
    this.chipSelectors = [];

    this.elements.forEach(e => { try { e.destroy(); } catch { /* ok */ } });
    this.elements = [];

    this.reelStripContainers = [];
    this.balanceText = null;
    this.lastWinText = null;
    this.winLineText = null;
    this.betDisplayText = null;
    this.linesDisplayText = null;
    this.betPerLineText = null;
    this.spinBtn = null;
    this.toggleContainer = null;
  }
}
